import type React from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import Typography from 'app/ui/components/atoms/typography';
import PopupDialog from 'app/ui/components/molecules/popup-dialog';
import { PlansCarouselModal } from 'app/ui/components/molecules/carousel-modal';
import { useStartProgressivePlan } from 'app/hooks/use-start-progressive-plan';

import Athlete1 from 'app/assets/training-plans/athlete1shadow.png';
import Athlete2 from 'app/assets/training-plans/athlete2shadow.png';
import Athlete3 from 'app/assets/training-plans/athlete3shadow.png';
import Athlete4 from 'app/assets/training-plans/athlete4.png';
import Stripe1 from 'app/assets/training-plans/stripe1.svg';
import Stripe2 from 'app/assets/training-plans/stripe2.svg';
import Stripe3 from 'app/assets/training-plans/stripe3.svg';
import Stripe4 from 'app/assets/training-plans/stripe4.svg';

const customBreakpoints = {
  mediumPlus: 695,
  mediumWide: 760,
  preLarge: 900,
};

const customMedia = {
  mediumPlus: (callback: () => string) => `
    @media (min-width: ${customBreakpoints.mediumPlus}px) {
      ${callback()}
    }
  `,
  mediumWide: (callback: () => string) => `
    @media (min-width: ${customBreakpoints.mediumWide}px) {
      ${callback()}
    }
  `,
  preLarge: (callback: () => string) => `
    @media (min-width: ${customBreakpoints.preLarge}px) {
      ${callback()}
    }
  `,
};

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 190px;
  overflow: hidden;
  border-radius: 8px;
  background: linear-gradient(-90deg, #554F02 0%, #161616 80%);
  cursor: pointer;
  &:hover {
    outline: 1px solid ${({ theme }) => theme.colors.focoltone};
  }

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        height: 240px;
      `,
    )}
  `}
`;

const StripeLayer1 = styled.img`
  position: absolute;
  top: 0;
  right: -20px;
  width: 90%;
  height: 190px;
  object-fit: contain;
  object-position: right top;
  pointer-events: none;
  z-index: 1;

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        right: 0;
        width: 70%;
        height: 100%;
        object-fit: cover;
        object-position: left top;
        z-index: 1;
      `,
    )}
    ${media.large(
      () => `
        width: 54%;
      `,
    )}
  `}
`;

const StripeLayer2 = styled.img`
  position: absolute;
  top: 56px;
  right: -20px;
  width: 90%;
  height: 134px;
  object-fit: contain;
  object-position: right top;
  pointer-events: none;
  z-index: 2;

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        right: 0;
        width: 70%;
        top: 68px;
        height: 172px;
        object-fit: cover;
        object-position: left top;
        z-index: 2;
      `,
    )}
    ${media.large(
      () => `
        width: 54%;
        top: 68px;
      `,
    )}
  `}
`;

const StripeLayer3 = styled.img`
  position: absolute;
  top: 104px;
  right: -20px;
  width: 90%;
  height: 86px;
  object-fit: contain;
  object-position: right top;
  pointer-events: none;
  z-index: 3;

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        right: 0;
        top: 124px;
        width: 70%;
        height: 116px;
        object-fit: cover;
        object-position: left top;
        z-index: 3;
      `,
    )}
    ${media.large(
      () => `
        top: 124px;
        width: 54%;
        z-index: 5;
      `,
    )}
  `}
`;

const StripeLayer4 = styled.img`
  position: absolute;
  top: 154px;
  right: -20px;
  width: 90%;
  height: 36px;
  object-fit: contain;
  object-position: right top;
  pointer-events: none;
  z-index: 4;

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        right: 0;
        top: 182px;
        width: 70%;
        height: 59px;
        object-fit: cover;
        object-position: left top;
        z-index: 4;
      `,
    )}
    ${media.large(
      () => `
        top: 182px;
        width: 54%;
        z-index: 8;
      `,
    )}
  `}
`;

const Athlete1Main = styled.div`
  position: absolute;
  top: -55px;
  right: -65px;
  left: auto;
  width: 250px;
  height: 260px;
  background-image: url(${Athlete1});
  background-size: contain;
  background-position: left bottom;
  background-repeat: no-repeat;
  z-index: 2;

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        top: -145px;
        right: -20px;
        width: 240px;
        height: 340px;
        z-index: 3;
      `,
    )}
    ${media.large(
      () => `
        top: -114px;
        left: 78%;
        right: auto;
        width: 299px;
        height: 352px;
      `,
    )}
  `}
`;

const Athlete2Main = styled.div`
  display: none;
  position: absolute;
  top: -15px;
  left: 52%;
  width: 334px;
  height: 281px;
  background-image: url(${Athlete2});
  background-size: contain;
  background-position: left center;
  background-repeat: no-repeat;
  z-index: 8;

  ${customMedia.preLarge(
    () => `
      display: block;
      left: 42%;
      z-index: 3;
    `,
  )}

  ${({ theme: { media } }) => `
    ${media.large(
      () => `
        left: 52%;
        z-index: 8;
      `,
    )}
  `}
`;

const Athlete3Main = styled.div`
  display: none;
  position: absolute;
  top: -7px;
  right: calc(59.5% - 270px);
  left: auto;
  width: 298px;
  height: 247px;
  background-image: url(${Athlete3});
  background-size: contain;
  background-position: left bottom;
  background-repeat: no-repeat;
  z-index: 25;

  ${customMedia.preLarge(
    () => `
      display: block;
      right: calc(69.5% - 270px);
    `,
  )}

  ${({ theme: { media } }) => `
    ${media.large(
      () => `
        right: calc(59.5% - 270px);
      `,
    )}
  `}
`;

const Athlete4Main = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: 10rem;
  width: 280px;
  height: 260px;
  background-image: url(${Athlete4});
  background-size: contain;
  background-position: left bottom;
  background-repeat: no-repeat;
  z-index: 5;

  ${customMedia.mediumPlus(
    () => `
      display: block;
    `,
  )}

  ${customMedia.mediumWide(
    () => `
      left: 16rem;
    `,
  )}

  ${customMedia.preLarge(
    () => `
      display: none;
    `,
  )}
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 70%;
  height: 100%;
  background: linear-gradient(to right, rgba(22, 22, 22, 0.9) 0%, rgba(0, 0, 0, 0) 100%);
  z-index: 10;

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        width: 60%;
        background: linear-gradient(to right, rgba(22, 22, 22, 0.8) 0%, rgba(0, 0, 0, 0) 100%);
      `,
    )}
    ${media.large(
      () => `
        width: 55%;
      `,
    )}
  `}
`;

const ContentWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  z-index: 11;
  max-width: 65%;

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        top: 56px;
        left: 24px;
        transform: none;
        max-width: 45%;
      `,
    )}
    ${media.large(
      () => `
        top: 68px;
        max-width: 35%;
      `,
    )}
  `}
`;

const TitleText = styled(Typography)`
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  margin: 0;
  text-transform: uppercase;
  line-height: 26px;
  letter-spacing: 0.11px;

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        font-size: 32px;
        line-height: 40px;
      `,
    )}
    ${media.large(
      () => `
        font-size: 40px;
        line-height: 44px;
      `,
    )}
  `}
`;

const DescriptionText = styled(Typography)`
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.09px;
  margin: 0;
  color: ${({ theme }) => theme.colors.lightGray};

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        font-size: 16px;
        line-height: 22px;
      `,
    )}
    ${media.large(
      () => `
        font-size: 18px;
        line-height: 24px;
      `,
    )}
  `}
`;

type StartPlanBannerProps = {
  planId: string;
};

const StartPlanBanner: React.FC<StartPlanBannerProps> = ({ planId }) => {
  const intl = useIntl();
  const theme = useTheme();

  const bannerTitle = intl.formatMessage({ id: 'library.trainingPlansBannerHeader' });
  const bannerDescription = intl.formatMessage({ id: 'library.trainingPlansBannerDescription' });

  const { handleStartPlanClick, handleStartPlanConfirm, isCarouselModalOpen, setIsCarouselModalOpen, dialogConfig } =
    useStartProgressivePlan({
      planId,
      planTitle: bannerTitle,
      trackingEventName: 'start_plan_banner_clicked',
    });

  return (
    <>
      <Container onClick={handleStartPlanClick}>
        <StripeLayer1 src={Stripe1} />
        <StripeLayer2 src={Stripe2} />
        <Athlete1Main />
        <StripeLayer3 src={Stripe3} />
        <Athlete2Main />
        <StripeLayer4 src={Stripe4} />
        <Athlete3Main />
        <Athlete4Main />
        <GradientOverlay />
        <ContentWrapper>
          <TitleText variant="h2" color={theme.colors.white}>
            {bannerTitle}
          </TitleText>
          <DescriptionText variant="body2">{bannerDescription}</DescriptionText>
        </ContentWrapper>
      </Container>

      <PopupDialog
        isOpen={!!dialogConfig}
        primaryText={dialogConfig?.primaryText || ''}
        primaryButton={dialogConfig?.primaryButton}
      />

      <PlansCarouselModal
        isOpen={isCarouselModalOpen}
        onOpenChange={setIsCarouselModalOpen}
        onStartPlan={handleStartPlanConfirm}
      />
    </>
  );
};

export default StartPlanBanner;
